import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp } from '@fortawesome/sharp-solid-svg-icons';
import React, { ReactNode, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { twMerge } from 'tailwind-merge';

import Button, { ButtonSize, ButtonVariant } from '../button';
import OutsideClickWrapper from '../OutsideClickWrapper';
import Typography from '../typography';

type Props = {
  className?: string;
  title: string | ReactNode;
  onClose?: () => void;
  onExpand?: () => void;
  parentButtonClassName?: string;
  /** Classnames to be applied when the button drop down is expanded */
  expandedClassName?: string;
  rows: ReactNode[];
  initializeOpen?: boolean | null;
  shouldCloseOnOutsideClick?: boolean;
  adjacentButtonToExpand?: ReactNode;
  isExpandable?: boolean;
};

/**
 * Button Navigation Component
 * - The rows of the button navigation can be set to any React Node or the ButtonNavigationItem can be used as in these examples
 */
const ButtonNavigation: React.FC<Props> = ({
  className = '',
  initializeOpen = null,
  onClose = undefined,
  onExpand = undefined,
  parentButtonClassName = undefined,
  expandedClassName = undefined,
  shouldCloseOnOutsideClick = false,
  adjacentButtonToExpand = null,
  isExpandable = true,
  title,
  rows,
}) => {
  const [expanded, setExpanded] = useState<boolean | null>(initializeOpen);

  useEffect(() => {
    if (expanded === false) {
      onClose?.();
    }
  }, [expanded, onClose]);

  return (
    <OutsideClickWrapper
      className="flex grow"
      onOutsideClick={() => {
        if (expanded && shouldCloseOnOutsideClick) {
          setExpanded(false);
        }
      }}
    >
      <div
        className={twMerge(
          'flex w-[200px] max-w-[200px] grow flex-col bg-gray-10 text-white',
          className
        )}
      >
        <div
          className={twMerge(
            'bg-white text-gray-100',
            'dark:bg-gray-100 dark:text-white',
            'px-3 py-1',
            'flex grow items-center justify-between ',
            parentButtonClassName,
            expanded && expandedClassName,
            isMobile ? 'min-h-[75px]' : 'max-h-[75px]'
          )}
          role="button"
          tabIndex={0}
          onClick={() => {
            if (!isExpandable) {
              return;
            }
            if (!expanded) {
              onExpand?.();
            }
            setExpanded((prev) => !prev);
          }}
          onKeyDown={(e) => {
            if (!isExpandable) {
              return;
            }
            if (e.key === 'Enter') {
              if (!expanded) {
                onExpand?.();
              }
              setExpanded((prev) => !prev);
            }
          }}
        >
          {typeof title === 'string' ? (
            <Typography variant="button2">{title}</Typography>
          ) : (
            title
          )}

          <div className="flex items-center gap-x-4">
            {isExpandable && (
              <Button size={ButtonSize.XS} variant={ButtonVariant.GHOST}>
                <FontAwesomeIcon
                  className={twMerge(
                    expanded ? ' rotate-0' : 'rotate-180',
                    'text-gray-70 transition-transform duration-300'
                  )}
                  icon={faChevronUp}
                />
              </Button>
            )}

            {adjacentButtonToExpand}
          </div>
        </div>
        {expanded && (
          <div className="animate-all z-[200] flex h-full flex-col overflow-auto sm:h-fit">
            {rows.map((row) => row)}
          </div>
        )}
      </div>
    </OutsideClickWrapper>
  );
};

export default ButtonNavigation;
